import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { UserSettingsModels } from '@share-models';
import { EnableEscCloseDirective, UiButtonComponent } from '@ui';
import { UserSettingsActions } from '../../data-access/state/user-account-settings.actions';
import { UserAccountSettingsState } from '../../data-access/state/user-account-settings.reducer';

@Component({
  selector: 'app-change-device-emails-modal',
  standalone: true,
  imports: [MatDialogClose, TranslatePipe, UiButtonComponent],
  templateUrl: './change-device-emails-modal.component.html',
  styleUrl: './change-device-emails-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class ChangeDeviceEmailsModalComponent {
  private dialogRef = inject(MatDialogRef<ChangeDeviceEmailsModalComponent>);
  private store$ = inject<Store<UserAccountSettingsState>>(Store);
  public data = inject<{ settings: UserSettingsModels.UserSettingsDocument }>(
    MAT_DIALOG_DATA,
  );

  public readonly deviceEmailChannels: UserSettingsModels.DeviceEmailChannelOption[] = [
    {
      key: UserSettingsModels.DeviceChannel.CAMERA,
      label: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.CAMERA_LABEL',
    },
    {
      key: UserSettingsModels.DeviceChannel.CORE,
      label: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.CORE_LABEL',
    },
  ];
  public readonly deviceEmailFrequencyOptions: UserSettingsModels.DeviceEmailFrequencyOption[] = [
    {
      value: UserSettingsModels.EmailFrequency.REAL_TIME,
      label: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.REAL_TIME',
    },
    {
      value: UserSettingsModels.EmailFrequency.DAILY,
      label: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.DAILY',
    },
    {
      value: UserSettingsModels.EmailFrequency.WEEKLY,
      label: 'SUAS.ACCOUNT.DETAILS.DEVICE_EMAILS.WEEKLY',
    },
  ];

  private selections: UserSettingsModels.DeviceStabilityUserOverride = {
    camera: this.normalizeFrequencies(
      this.data?.settings?.deviceStability?.[UserSettingsModels.DeviceChannel.CAMERA],
    ),
    core: this.normalizeFrequencies(
      this.data?.settings?.deviceStability?.[UserSettingsModels.DeviceChannel.CORE],
    ),
  };

  public isOrgDefault(channel: UserSettingsModels.DeviceChannel): boolean {
    return (this.selections[channel] ?? []).length === 0;
  }

  public isChecked(
    channel: UserSettingsModels.DeviceChannel,
    frequency: UserSettingsModels.EmailFrequency,
  ): boolean {
    return (this.selections[channel] ?? []).includes(frequency);
  }

  public selectOrgDefault(channel: UserSettingsModels.DeviceChannel): void {
    this.selections = { ...this.selections, [channel]: [] };
  }

  public toggleFrequency(
    channel: UserSettingsModels.DeviceChannel,
    frequency: UserSettingsModels.EmailFrequency,
  ): void {
    const current = this.selections[channel] ?? [];
    const next = current.includes(frequency)
      ? current.filter((value) => value !== frequency)
      : [...current, frequency];
    this.selections = { ...this.selections, [channel]: next };
  }

  public close(): void {
    this.dialogRef.close();
  }

  public save(): void {
    const deviceStability: UserSettingsModels.DeviceStabilityUserOverride = {};
    for (const channel of this.deviceEmailChannels) {
      const values = this.selections[channel.key] ?? [];
      if (values.length) {
        deviceStability[channel.key] = values;
      }
    }
    this.store$.dispatch(
      UserSettingsActions.startUpdateUserSettingsProcess({
        userSettings: { deviceStability },
      }),
    );
    this.dialogRef.close();
  }

  private normalizeFrequencies(
    value: UserSettingsModels.EmailFrequency[] | UserSettingsModels.EmailFrequency | undefined,
  ): UserSettingsModels.EmailFrequency[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return [value];
    }
    return [];
  }
}
